import { AfterViewInit, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/services/auth.service';
import { DesktopUnderwriterService } from '../../../borrower-readiness/services/desktop-underwriter.service';
import { UrlConstants } from '../../../constants/url-constants';
import { CreditReportService } from '../../../credit-report/services/credit-report.service';
import { LockerCancellationDialogLoginPageComponent } from '../../../locker-cancellation-dialog/components/locker-cancellation-dialog/locker-cancellation-dialog-login-page.component'; // eslint-disable-line max-len
import { Features } from '../../../models/features/features.model';
import { FinancialInstitutionSummary } from '../../../models/institution-enrollment/financial-institution-summary.model';
import { IWindowSnare } from '../../../models/interfaces/i-window-snare.interface';
import { PropertyV2 } from '../../../models/properties/property-v2.model';
import { Logo } from '../../../models/themes/logo.model';
import { Theme } from '../../../models/themes/theme.model';
import { TrackableItemClick } from '../../../models/trackable-item-click.model';
import { UserConfiguration } from '../../../models/user-configuration.model';
import { BrowserWarningComponent } from '../../../shared-components/components/browser-warning/browser-warning.component';
import { PropertiesSearchURLResponse } from '../../../shared-components/models/property-search-url-response.model';
import { FeatureCode } from '../../../shared-enums/feature-code.enum';
import { WINDOW } from '../../../shared-providers/window.providers';
import { FeaturesService } from '../../../shared-services/services/features.service';
import { PropertyService } from '../../../shared-services/services/property.service';
import { SessionStorageService } from '../../../shared-services/services/session-storage.service';
import { ThemeService } from '../../../shared-services/services/theme.service';
import { TrackableItemService } from '../../../shared-services/services/trackable-item.service';
import { MarginalDirective } from '../marginal';
import { GlobalCTATrackerService } from 'app/shared-services/services/global-cta-tracker.service';
import { FreshDeskService } from 'app/shared-services/services/freshdesk.service';

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    standalone: false
})
export class FooterComponent extends MarginalDirective implements OnInit, AfterViewInit {
    @Input() companyDisclaimerMessage = [];
    @Input() creditRoute: string;
    @Input() displayExtraFooter: boolean;
    @Input() features: Features;
    @Input() financialInstitution: FinancialInstitutionSummary[];
    @Input() isLockerLiteUser = false;
    @Input() isOldFooter = true;
    @Input() isRegistrationFooter = false;
    @Input() isValuePropositionPage = false;
    @Input() property: PropertyV2;
    @Input() showDisclaimer = false;
    @Input() theme: Theme;
    @Input() userConfiguration: UserConfiguration;
    @Input() isFromMobileApp = false;

    @Output() navigateToRegistration: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() navigateToSignIn: EventEmitter<boolean> = new EventEmitter<boolean>();

    public showCalculator = false;

    public androidUrl: string;
    public companyName: string;
    public iosUrl: string;

    public now: number = Date.now();

    public freshDeskUrl: string = '';
    public logo: Logo;

    constructor(
        private authService: AuthService,
        private creditReportService: CreditReportService,
        private desktopUnderwriterService: DesktopUnderwriterService,
        private dialog: MatDialog,
        public featuresService: FeaturesService,
        private freshDeskService: FreshDeskService,
        private globalCTATrackerService: GlobalCTATrackerService,
        private propertyService: PropertyService,
        private router: Router,
        public sessionStorageService: SessionStorageService,
        public themeService: ThemeService,
        private title: Title,
        private trackableItemService: TrackableItemService,
        @Inject(WINDOW) window: IWindowSnare
    ) {
        super(themeService, sessionStorageService, window);
    }

    ngOnInit(): void {
        this.themeAvailable();
        this.getFreshDesk();

        if (this.features && this.features?.featureCodes?.includes(FeatureCode.HomeAffordabilityCalculator)) {
            this.showCalculator = true;
        }
    }

    ngAfterViewInit(): void {
        const isSupported = this.isBrowserSupported();

        if (!isSupported) {
            this.dialog.open(BrowserWarningComponent, {
                disableClose: true,
                minWidth: '35vw',
                autoFocus: false
            });
        }
    }

    ngOnDestroy(): void {
        this.unsubscriber.next(null);
        this.unsubscriber.complete();
    }

    public get FeatureCode(): typeof FeatureCode {
        return FeatureCode;
    }

    public onPropertySearchClick(): void {
        const newWindow = window.open('', '_blank');

        this.propertyService.getHomePropertySearchUrl()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((response: PropertiesSearchURLResponse) => {
                if (response.isSuccess) {
                    this.globalCTATrackerService.eventTrackerV2('footer-cta', 'Property Search');

                    const trackableItemClick = new TrackableItemClick({
                        trackableItemCode: 'HomeScout'
                    });
                    this.trackableItemService.registerClick(trackableItemClick);

                    if (response.result.openInternally) {
                        if (newWindow) newWindow.location.href = response.result.webLoginUrl;
                    } else {
                        if (response?.result?.loginUrl) {
                            if (newWindow) newWindow.location.href = response.result.loginUrl;
                        } else {
                            newWindow?.close();
                        }
                    }
                } else {
                    newWindow?.close();
                }
            });
    }

    public isBrowserSupported(): boolean {
        const ua = window.navigator.userAgent;

        const isIe10OrOlder = ua.indexOf('MSIE ') >= 0;
        const isIe11 = ua.indexOf('Trident/') >= 0;

        return !isIe10OrOlder && !isIe11;
    }

    public themeAvailable(): void {
        this.title.setTitle(this.theme?.titleBar);
        this.iosUrl = this.setIosUrl();
        this.androidUrl = this.setAndroidUrl();
    }

    public getFreshDesk(): void {
        this.freshDeskService.getFreshDeskSSOUrl()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((response) => {
                if (response) {
                    this.freshDeskUrl = response.supportCenterUrl;
                }
            });
    }

    private setIosUrl(): string {
        if (this.theme?.themeLoginPage && this.theme?.themeLoginPage?.iosUrl) {
            return this.theme.themeLoginPage.iosUrl;
        } else {
            return '';
        }
    }

    private setAndroidUrl(): string {
        if (this.theme?.themeLoginPage && this.theme?.themeLoginPage?.androidUrl) {
            return this.theme.themeLoginPage.androidUrl;
        } else {
            return '';
        }
    }

    public freshdeskLogin(): void {
        this.clickEvent('Open Fresh Desk Support');

        window.open(this.freshDeskUrl ?? UrlConstants.freshdeskWebUrl, '_blank');
    }
    public openCancellationModal(): void {
        this.clickEvent('Open Locker Cancellation');

        this.dialog.open(LockerCancellationDialogLoginPageComponent, {
            disableClose: false,
            autoFocus: false,
            maxHeight: '95vh',
            maxWidth: '80vw',
            width: '600px'
        });
    }

    public route(destination: string, subDestination: string, cta: string): void {
        if (subDestination === 'Credit Score') {
            this.creditReportService.setCreditHealthRouterTab(0);
        }

        if (subDestination === 'Credit Report') {
            this.creditReportService.setCreditHealthRouterTab(1);
        }

        if (subDestination === 'Credit Compass') {
            this.creditReportService.setCreditHealthRouterTab(2);
        }

        if (subDestination === 'Credit Simulator') {
            this.creditReportService.setCreditHealthRouterTab(3);
        }

        if (subDestination === 'Desktop Underwriter') {
            this.desktopUnderwriterService.setMortgageReadinessTab(1);
        }

        if (subDestination === 'Saved Properties') {
            this.propertyService.setPropertyRouterTab(0);
        }

        if (cta) {
            this.globalCTATrackerService.eventTrackerV2('footer-cta', cta);
        }

        this.router.navigate([destination]);
    }

    public sendFeedBack(url: string): void {
        window.open(url, '_blank');
    }

    public navigateToSignInPage(): void {
        this.navigateToSignIn.emit(true);
    }

    public startRegistration(): void {
        this.navigateToRegistration.emit(true);
    }

    public isLenderConnectEnabled(): boolean {
        return this.featuresService.isFeatureEnabled(FeatureCode.VerificationService)
            || (this.featuresService.isFeatureEnabled(FeatureCode.ShowLender)
                && this.featuresService.isFeatureEnabled(FeatureCode.LenderLockerSharing));
    }

    public onDisclaimerClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        if (target.closest('.pointer-cursor')) {
            this.route('/contact-lender', '', 'Contact Lender');
        }
    }

    public clickEvent(cta: string): void {
        this.globalCTATrackerService.unAuthenticatedEventTrackerV2('footer-cta', cta);
    }
}
